<script setup lang="ts">
import { ref } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { Loader2, Save, Check, ChevronDown, Clock } from "lucide-vue-next";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const router = useRouter();
const store = useResultsV2Store();
const populationsStore = useResultsV2PopulationsStore();
const { hasUnsavedChanges, saving, isDefaultView } = storeToRefs(store);

const showNameDialog = ref(false);
const newViewName = ref("");

const showEphemeralWarning = ref(false);
const pendingSaveAction = ref<"save" | "saveAsNew" | null>(null);

function handleSave() {
  if (populationsStore.hasEphemeral()) {
    pendingSaveAction.value = isDefaultView.value ? "saveAsNew" : "save";
    showEphemeralWarning.value = true;
    return;
  }
  if (isDefaultView.value) {
    // Default view can't be saved directly — prompt for name
    handleSaveAsNew();
  } else {
    store.saveView();
  }
}

function handleSaveAsNew() {
  if (populationsStore.hasEphemeral()) {
    pendingSaveAction.value = "saveAsNew";
    showEphemeralWarning.value = true;
    return;
  }
  newViewName.value = "";
  showNameDialog.value = true;
}

function proceedWithoutEphemeral() {
  showEphemeralWarning.value = false;
  if (pendingSaveAction.value === "save") {
    store.saveView();
  } else {
    newViewName.value = "";
    showNameDialog.value = true;
  }
  pendingSaveAction.value = null;
}

async function confirmSaveAsNew() {
  if (!newViewName.value.trim()) return;
  const newView = await store.saveAsNew(newViewName.value.trim());
  showNameDialog.value = false;
  if (newView) {
    router.push({ name: "SurveyResultsV2View", params: { viewToken: newView.token } });
  }
}
</script>

<template>
  <div class="flex items-center">
    <!-- Primary save button -->
    <Button
      v-if="!isDefaultView"
      :disabled="!hasUnsavedChanges || saving"
      :variant="hasUnsavedChanges ? 'default' : 'outline'"
      class="rounded-r-none"
      @click="handleSave">
      <Loader2 v-if="saving" class="mr-2 h-4 w-4 animate-spin" />
      <Save v-else-if="hasUnsavedChanges" class="mr-2 h-4 w-4" />
      <Check v-else class="mr-2 h-4 w-4" />
      {{ saving ? "Saving..." : hasUnsavedChanges ? "Save" : "Saved" }}
    </Button>

    <!-- Dropdown for "Save as new" -->
    <DropdownMenu v-if="!isDefaultView">
      <DropdownMenuTrigger as-child>
        <Button
          :variant="hasUnsavedChanges ? 'default' : 'outline'"
          class="rounded-l-none border-l-0 px-2"
          :disabled="saving">
          <ChevronDown class="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem @click="handleSaveAsNew">
          Save as new
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Default view: only "Save as new" -->
    <Button
      v-if="isDefaultView"
      :variant="hasUnsavedChanges ? 'default' : 'outline'"
      @click="handleSaveAsNew">
      Save as new
    </Button>
  </div>

  <!-- Temporary populations warning: they are never saved with the dashboard -->
  <Dialog v-model:open="showEphemeralWarning">
    <DialogContent class="sm:max-w-[440px]">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Clock class="h-4 w-4 text-status-warning-strong" />
          Temporary populations
        </DialogTitle>
        <DialogDescription>
          This dashboard uses temporary populations that are not saved. They will not be part
          of the saved dashboard: save them first from "Edit populations", or continue without them.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="showEphemeralWarning = false">Cancel</Button>
        <Button @click="proceedWithoutEphemeral">Save without them</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Name dialog for "Save as new" -->
  <Dialog v-model:open="showNameDialog">
    <DialogContent class="sm:max-w-[400px]">
      <DialogHeader>
        <DialogTitle>Save as new dashboard</DialogTitle>
        <DialogDescription>
          Give a name to your new dashboard.
        </DialogDescription>
      </DialogHeader>
      <Input
        v-model="newViewName"
        placeholder="Dashboard name..."
        @keydown.enter="confirmSaveAsNew" />
      <DialogFooter>
        <Button variant="outline" @click="showNameDialog = false">Cancel</Button>
        <Button :disabled="!newViewName.trim()" @click="confirmSaveAsNew">Save</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
